import { Divider, TableCell } from '@mui/material';
import { Tag } from '@strength/domain';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import {
  MultiSelect,
  SelectableOption,
} from '../../../components/input/multi-select';
import { SearchInput } from '../../../components/input/search-input';
import { useViewer } from '../../../context/viewer';
import { LibraryWorkoutTemplate } from '../../library/workout-templates/types';
import { AdvancedSearchModal } from './AdvancedSearchModal';
import { AdvancedSearchTable } from './AdvancedSearchTable';
import { useAuth } from '../../../../auth';
import { getTrpcProxyClient } from '../../../../trpc';
import { useQuery } from '@tanstack/react-query';

export const AdvancedWorkoutTemplateSearchModal = ({
  workoutName,
  open,
  onClose,
  onAddWorkoutTemplate,
}: {
  workoutName: string;
  open: boolean;
  onClose: () => void;
  onAddWorkoutTemplate: (workoutTemplate: LibraryWorkoutTemplate) => void;
}) => {
  const { t } = useTranslation();
  const {
    team: { selectedTeam },
  } = useViewer();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [selectedExerciseOptions, setSelectedExerciseOptions] = useState<
    SelectableOption[]
  >([]);

  const workoutTemplatesQuery =
    trpc.workoutTemplateLibrary.templateSearch.useQuery(
      {
        searchTerm,
        tagIds: selectedTags.map((f) => f.id),
        exerciseIds: selectedExerciseOptions.map((option) => option.id),
      },
      {
        enabled: !!selectedTeam,
        staleTime: 10_000,
      },
    );
  const workoutTemplates = workoutTemplatesQuery.data?.records ?? [];

  const tagsQuery = trpc.tag.tags.useQuery({
    types: ['workout_template'],
  });
  const tagOptions = tagsQuery.data || [];

  // fetch exercises used in templates (deduped — these ids are sent in the
  // exerciseOptionsByIds GET query string, which overflows on large libraries)
  const referenceExerciseIds = [
    ...new Set(workoutTemplates.flatMap((r) => r.exerciseIds)),
  ];

  // This allows us to treat the mutate call as a GET request so caching
  // and the `enabled` flag works correctly
  const auth = useAuth();
  const trpcClient = getTrpcProxyClient(() => auth.setAuth(null));
  const { data: exerciseOptions } = useQuery({
    queryKey: ['exerciseOptionsByIds', referenceExerciseIds],
    queryFn: () =>
      trpcClient.exerciseLibrary.exerciseOptionsByIds.mutate({
        libraryExerciseIds: referenceExerciseIds,
      }),
    enabled: referenceExerciseIds.length > 0,
  });

  const uniqueExerciseOptions = useMemo(
    () =>
      exerciseOptions?.filter(
        (e, index, self) =>
          index === self.findIndex((exercise) => exercise.id === e.id),
      ) ?? [],
    [exerciseOptions],
  );

  return (
    <AdvancedSearchModal<LibraryWorkoutTemplate>
      id={workoutName}
      modalTitle={t('workoutTemplate')}
      galleryTitle={t('workoutTemplates')}
      open={open}
      onClose={onClose}
      onAddItem={onAddWorkoutTemplate}
      items={workoutTemplates}
      isLoading={workoutTemplatesQuery.isLoading}
      isError={workoutTemplatesQuery.isError}
      renderTableView={(items, selectedItem, setSelected) => (
        <AdvancedSearchTable<LibraryWorkoutTemplate>
          items={items}
          selectedItem={selectedItem}
          setSelected={setSelected}
          onAddItem={onAddWorkoutTemplate}
          title={t('workoutTemplates')}
          renderAdditionalColumnHeaders={() => (
            <TableCell>{t('tags')}</TableCell>
          )}
          renderAdditionalColumns={(item) => (
            <TableCell>{item.tags.map((t) => t.tag.name).join(', ')}</TableCell>
          )}
        />
      )}
      filters={
        <>
          <div>
            <SearchInput
              fullWidth
              value={searchTerm}
              onChange={(v) => setSearchTerm(v)}
            />
          </div>
          <Divider sx={{ my: 1, mb: 1 }} flexItem />
          <div>
            <MultiSelect
              fullWidth
              selectedOptions={selectedTags}
              options={tagOptions}
              onChange={setSelectedTags}
              optionToName={(option) => option.name}
              placeholder={t('allTags')}
              label={t('tags')}
            />
          </div>
          <div>
            <MultiSelect
              fullWidth
              selectedOptions={selectedExerciseOptions}
              options={uniqueExerciseOptions}
              placeholder={t('all')}
              label={t('includesExercise')}
              onChange={setSelectedExerciseOptions}
              optionToName={(e) => e.name}
            />
          </div>
        </>
      }
    />
  );
};
